/**
 * PolicyOverviewTab — read-only summary of policy metadata & contract.
 *
 * Shows exactly what `requireBusinessPolicy` evaluates:
 *   scope (verticals + actions + applyTo), combinator, priority, audit
 *   config, onDeny contract (statusCode / errorCode / message), tags.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from '../../../shared/ui/Card';
import type { PolicyRecord } from '../../../features/business-policies/types';

export function PolicyOverviewTab({
  record,
}: {
  record: PolicyRecord;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const meta = record.document.metadata;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-biz-section">
      <Card>
        <CardHeader>
          <h3 className="biz-heading font-semibold">{t('overview.scopeTitle')}</h3>
        </CardHeader>
        <CardBody className="space-y-biz-content-lg">
          <FieldRow label={t('overview.applyTo')}>
            <span className="inline-flex items-center px-biz-content py-0.5 rounded-biz-full border border-biz-border bg-biz-muted font-mono text-biz-xs">
              {record.document.scope.applyTo}
            </span>
          </FieldRow>
          <FieldRow label={t('overview.verticals')}>
            {record.scopeVerticalKeys.length === 0 ? (
              <span className="text-biz-muted-foreground">{t('overview.allVerticals')}</span>
            ) : (
              <div className="flex flex-wrap gap-biz-content">
                {record.scopeVerticalKeys.map((v) => (
                  <Chip key={v}>{v}</Chip>
                ))}
              </div>
            )}
          </FieldRow>
          <FieldRow label={t('overview.actions')}>
            <div className="flex flex-wrap gap-biz-content">
              {record.scopeActions.map((a) => (
                <Chip key={a}>{a}</Chip>
              ))}
            </div>
          </FieldRow>
          <FieldRow label={t('overview.combinator')}>
            <Chip>{record.combinator}</Chip>
          </FieldRow>
          <FieldRow label={t('overview.priority')}>
            <span className="biz-numeric font-semibold">{record.priority}</span>
          </FieldRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="biz-heading font-semibold">{t('overview.denyTitle')}</h3>
        </CardHeader>
        <CardBody className="space-y-biz-content-lg">
          <FieldRow label={t('overview.statusCode')}>
            <span className="biz-numeric font-semibold">{record.onDeny.statusCode}</span>
          </FieldRow>
          <FieldRow label={t('overview.errorCode')}>
            <span className="font-mono text-biz-sm">{record.onDeny.errorCode}</span>
          </FieldRow>
          <FieldRow label={t('overview.message')}>
            <p className="text-biz-sm">{record.onDeny.message}</p>
          </FieldRow>
          {record.onDeny.messageKey ? (
            <FieldRow label={t('overview.messageKey')}>
              <span className="font-mono text-biz-xs">{record.onDeny.messageKey}</span>
            </FieldRow>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="biz-heading font-semibold">{t('overview.auditTitle')}</h3>
        </CardHeader>
        <CardBody className="space-y-biz-content-lg">
          <FieldRow label={t('overview.auditLevel')}>
            <Chip>{record.audit.level}</Chip>
          </FieldRow>
          <FieldRow label={t('overview.auditSeverity')}>
            <Chip>{record.audit.severity}</Chip>
          </FieldRow>
          <FieldRow label={t('overview.logDenial')}>
            <Chip>{record.audit.logDenial ? t('common.yes') : t('common.no')}</Chip>
          </FieldRow>
          <FieldRow label={t('overview.logAllow')}>
            <Chip>{record.audit.logAllow ? t('common.yes') : t('common.no')}</Chip>
          </FieldRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="biz-heading font-semibold">{t('overview.metaTitle')}</h3>
        </CardHeader>
        <CardBody className="space-y-biz-content-lg">
          <FieldRow label={t('overview.version')}>
            <span className="font-mono biz-numeric">{record.version}</span>
          </FieldRow>
          <FieldRow label={t('overview.createdAt')}>
            {new Date(record.createdAt).toLocaleString()}
          </FieldRow>
          <FieldRow label={t('overview.updatedAt')}>
            {new Date(record.updatedAt).toLocaleString()}
          </FieldRow>
          {record.activatedAt ? (
            <FieldRow label={t('overview.activatedAt')}>
              {new Date(record.activatedAt).toLocaleString()}
            </FieldRow>
          ) : null}
          {record.deprecatedAt ? (
            <FieldRow label={t('overview.deprecatedAt')}>
              {new Date(record.deprecatedAt).toLocaleString()}
            </FieldRow>
          ) : null}
          {meta?.tags && meta.tags.length > 0 ? (
            <FieldRow label={t('overview.tags')}>
              <div className="flex flex-wrap gap-biz-content">
                {meta.tags.map((tag) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
              </div>
            </FieldRow>
          ) : null}
          {meta?.relatedRegulation ? (
            <FieldRow label={t('overview.regulation')}>
              <span className="text-biz-sm">{meta.relatedRegulation}</span>
            </FieldRow>
          ) : null}
          <FieldRow label={t('overview.checksum')}>
            <span className="font-mono text-biz-xs text-biz-muted-foreground break-all">
              {record.checksumSha256}
            </span>
          </FieldRow>
        </CardBody>
      </Card>
    </div>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-biz-content-lg items-start">
      <dt className="text-biz-xs font-medium text-biz-muted-foreground uppercase tracking-wider pt-1">
        {label}
      </dt>
      <dd className="text-biz-sm">{children}</dd>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center px-biz-content py-0.5 rounded-biz-full border border-biz-border bg-biz-muted font-mono text-biz-xs">
      {children}
    </span>
  );
}
